/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import { useMemo } from 'react';
import { useQuery } from 'react-apollo';

import { usePageContentId } from '@confluence/page-context';
import { ContentStatus } from '@confluence/page-utils/entry-points/enums';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';

import { normalizeContentCreatorData } from '../normalizers/normalizeContentCreatorData';

import type {
	useContentCreatorDetailsPanelContentCreatedByQuery as useContentCreatorDetailsPanelContentCreatedByQuery$data,
	useContentCreatorDetailsPanelContentCreatedByQueryVariables as useContentCreatorDetailsPanelContentCreatedByQuery$variables,
} from './__types__/useContentCreatorDetailsPanelContentCreatedByQuery';
import { useContentOwner } from './useContentOwner';

const useContentCreatorDetailsPanelContentCreatedByQuery = graphql`
	query useContentCreatorDetailsPanelContentCreatedByQuery($contentId: ID!, $status: String!) {
		content(id: $contentId, status: [$status]) {
			nodes {
				id
				history {
					createdBy {
						type
						displayName
						... on KnownUser {
							accountId
						}
						profilePicture {
							path
						}
					}
				}
			}
		}
	}
`;
export const useContentCreator = () => {
	const [contentId] = usePageContentId();

	const {
		contentStatus,
		loading: isContentStatusLoading,
		error: contentStatusError,
		refetch: refetchContentStatus,
	} = useContentStatus();

	const isContentStatusDraft = contentStatus === ContentStatus.DRAFT;

	const {
		data: createdByData,
		loading: isCreatedByLoading,
		error: createdByError,
		refetch: refetchCreatedBy,
	} = useQuery<
		useContentCreatorDetailsPanelContentCreatedByQuery$data,
		useContentCreatorDetailsPanelContentCreatedByQuery$variables
	>(useContentCreatorDetailsPanelContentCreatedByQuery, {
		variables: {
			contentId: contentId ?? '',
			status: contentStatus ?? '',
		},
		skip:
			isContentStatusLoading ||
			!!contentStatusError ||
			!contentStatus ||
			!contentId ||
			isContentStatusDraft,
	});
	const normalizedCreatedByData = useMemo(
		() => normalizeContentCreatorData(createdByData),
		[createdByData],
	);

	const {
		data: contentOwnerData,
		loading: isContentOwnerLoading,
		error: contentOwnerError,
		refetch: refetchContentOwner,
	} = useContentOwner({
		skip: !isContentStatusDraft,
	});

	const refetchUserData = isContentStatusDraft ? refetchContentOwner : refetchCreatedBy;

	return {
		data: isContentStatusDraft ? contentOwnerData : normalizedCreatedByData,
		loading: isContentStatusLoading || isCreatedByLoading || isContentOwnerLoading,
		error: contentStatusError || createdByError || contentOwnerError,
		refetch: contentStatusError ? refetchContentStatus : refetchUserData,
	};
};
